import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoTexto from '@/components/Formulario/CampoTexto';
import { alterarDhcpRange, separarDhcpRange } from '@/lib/util';
import Grid, { GridItem } from '@/components/layout/Grid';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';


import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import { Address4 } from 'ip-address';
import HBox from '@/components/layout/HBox';
import Icon from '@mdi/react';
import { mdiCancel, mdiCheck } from '@mdi/js';
import React from 'react';
import { Accordion } from '@/components/layout/Accordion';
import CampoNumero from '@/components/Formulario/CampoNumero';
import { useQuery } from 'react-query';
import axios from 'axios';
import type {
  NetworkLanDTO,
  NetworkLanTypeDTO,
  NetworkWanDTO,
} from '@webip/shared/src/dto/appliance';
import { HotspotDTO } from '@webip/shared/src/dto/hotspot';
import AvisoVlanRadius from './AvisoVlanRadius';

const PORTAS_PROPRIAS = '__portas';

export const FormLan = ({
  value,
  onChange,
  fieldErrors,
  availablePorts,
  applianceId,
  lanBases = [],
  wans,
}: {
  value: NetworkLanDTO;
  onChange: (v: NetworkLanDTO) => void;
  fieldErrors: any;
  availablePorts: string[];
  applianceId?: number;
  lanBases?: NetworkLanDTO[];
  /** WANs da appliance para o link preferencial — undefined esconde o campo (OPNsense). */
  wans?: NetworkWanDTO[];
}) => {
  const dhcpRange = separarDhcpRange(value?.dhcp_range);

  const isVlan = (value?.vlan ?? 0) > 0;
  // Rede base só faz sentido com VLAN: sem ela a rede ficaria na mesma
  // interface da base, sem tag.
  const podeTerBase = isVlan && lanBases.length > 0;
  const lanBaseId = podeTerBase ? value?.lan_base_id ?? null : null;

  // Link preferencial só faz sentido com mais de uma WAN. Um já gravado
  // continua visível (e removível) mesmo se a segunda WAN sumiu; apontando
  // pra uma WAN excluída, aparece vazio.
  const wanOptions = (wans ?? []).map(w => ({
    id: String(w.id),
    label: w.uso === 'primario' ? w.nome : `${w.nome} (backup)`,
  }));
  const afinidadeAtual = wanOptions.some(o => o.id === String(value?.wan_afinidade_id))
    ? String(value!.wan_afinidade_id)
    : null;
  const multiWan = wanOptions.length > 1;

  const queryHotspots = useQuery<HotspotDTO[]>(
    ['hotspot-list'],
    async () => {
      const res = await axios.get<{ ok: boolean; result: HotspotDTO[] }>(
        '/api/hotspot',
        { withCredentials: true },
      );
      return res.data.result ?? [];
    },
    { staleTime: 60_000, refetchOnWindowFocus: false },
  );

  return (
    <Grid>
      <div style={{ position: 'absolute', height: 0 }}>
        <input
          type="text"
          name="username"
          style={{
            height: 0,
            width: 0,
            border: 'none',
            outline: 'none',
            margin: 0,
            padding: 0,
          }}
        />
        <input
          type="password"
          name="password"
          style={{
            height: 0,
            width: 0,
            border: 'none',
            outline: 'none',
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      <GridItem sm={12}>
        <Accordion title="Geral" startOpen>
          <Grid>
            <GridItem sm={12} md={4}>
              <CampoSeletor
                noSearch
                label="Ícone"
                value={value?.tipoLan}
                onChange={v =>
                  onChange({ ...value, tipoLan: v as NetworkLanTypeDTO })
                }
                options={[
                  { id: 'lan', label: 'LAN' },
                  { id: 'wifi', label: 'LAN Wi-fi' },
                  { id: 'server', label: 'LAN Servidores' },
                ]}
                error={fieldErrors?.tipoLan}
              />
            </GridItem>
            <GridItem sm={12} md={8}>
              <CampoTexto
                value={value?.nome ?? ''}
                onChange={v => onChange({ ...value, nome: v })}
                label="Nome"
                error={fieldErrors?.nome}
              />
            </GridItem>

            <GridItem sm={12} md={4}>
              <CampoNumero
                value={value?.vlan?.toString() ?? '0'}
                onChange={v => onChange({ ...value, vlan: v ? +v : 0 })}
                label="ID VLAN"
                hint={
                  isVlan
                    ? 'VLAN sobre a interface-base ao lado'
                    : '0 = interface dedicada'
                }
                error={fieldErrors?.vlan}
              />
            </GridItem>
            {podeTerBase && (
              <GridItem sm={12} md={8}>
                <CampoSeletor
                  noSearch
                  label="Interface base"
                  value={lanBaseId != null ? String(lanBaseId) : PORTAS_PROPRIAS}
                  onChange={v =>
                    onChange({
                      ...value,
                      lan_base_id: !v || v === PORTAS_PROPRIAS ? null : +v,
                      portas: !v || v === PORTAS_PROPRIAS ? value?.portas ?? [] : [],
                    })
                  }
                  options={[
                    { id: PORTAS_PROPRIAS, label: 'Portas próprias' },
                    ...lanBases.map(l => ({
                      id: String(l.id),
                      label: `Rede ${l.nome} (${l.portas?.join(', ') || '-'})`,
                    })),
                  ]}
                  hint={
                    lanBaseId != null
                      ? 'A VLAN usa as mesmas portas da rede escolhida (trunk)'
                      : 'Escolha uma rede para usar as mesmas portas dela como trunk'
                  }
                  error={fieldErrors?.lan_base_id}
                />
              </GridItem>
            )}
            {lanBaseId == null && (
              <GridItem sm={12} md={podeTerBase ? 12 : 8}>
                <CampoMultiSeletor
                  options={availablePorts.map(it => ({ label: it, id: it }))}
                  value={value?.portas}
                  onChange={v => onChange({ ...value, portas: v })}
                  label={isVlan ? 'Interface base (VLAN trunk)' : 'Portas'}
                  error={
                    fieldErrors &&
                    fieldErrors[
                    Object.keys(fieldErrors).find(k => k.startsWith('portas'))!
                    ]
                  }
                />
              </GridItem>
            )}
            {applianceId != null && (
              <GridItem sm={12} md={12}>
                <AvisoVlanRadius applianceId={applianceId} redeId={value?.id} vlan={value?.vlan} />
              </GridItem>
            )}
            <GridItem sm={12}>
              <CampoMultiTexto
                value={value?.ips}
                onChange={v =>
                  onChange({
                    ...value,
                    ips: v,
                  })
                }
                label="IPs/Subnets"
                error={
                  fieldErrors &&
                  fieldErrors[
                  Object.keys(fieldErrors).find(k => k.startsWith('ips'))!
                  ]
                }
                placeholder="Ex: 192.168.88.1"
              />
            </GridItem>
            {wans && (
              <GridItem sm={12}>
                <CampoSeletor
                  noSearch
                  nullable
                  label="Link WAN preferencial"
                  value={afinidadeAtual}
                  onChange={v => onChange({ ...value, wan_afinidade_id: v ? +v : null })}
                  options={wanOptions}
                  disabled={!multiWan && afinidadeAtual == null}
                  placeholder={multiWan ? 'Padrão (balanceamento/primário)' : 'Requer mais de uma WAN'}
                  hint="Toda a rede sai por este link; se ele cair, segue pelos demais. A afinidade de WAN de um dispositivo da rede tem prioridade."
                  error={fieldErrors?.wan_afinidade_id}
                />
              </GridItem>
            )}
          </Grid>
        </Accordion>
      </GridItem>

      <GridItem sm={12}>
        <Accordion
          title={
            <HBox>
              <span>DHCP</span>
              <Icon
                path={Boolean(value?.dhcp_range) ? mdiCheck : mdiCancel}
                size={0.75}
              />
            </HBox>
          }
        >
          <Grid>
            <GridItem sm={12}>
              <CampoCheckbox
                value={Boolean(value?.dhcp_range)}
                onChange={v =>
                  onChange({
                    ...value,
                    dhcp_range: v ? '-' : null,
                  })
                }
                text="DHCP"
                error={fieldErrors?.dhcp}
              />
            </GridItem>
            {!value.dhcp_range ? null : (
              <>
                <GridItem sm={12} md={6}>
                  <CampoTexto
                    value={dhcpRange[0] ?? ''}
                    onChange={v =>
                      onChange({ ...value, dhcp_range: alterarDhcpRange(value.dhcp_range, 0, v) })
                    }
                    label="Início DHCP"
                    error={fieldErrors?.dhcp_range}
                    placeholder={`Ex: ${!value?.ips?.length || !Address4.isValid(value.ips[0]) ? null : new Address4(value.ips[0]).startAddressExclusive().address} +`}
                  />
                </GridItem>
                <GridItem sm={12} md={6}>
                  <CampoTexto
                    value={dhcpRange[1] ?? ''}
                    onChange={v =>
                      onChange({ ...value, dhcp_range: alterarDhcpRange(value.dhcp_range, 1, v) })
                    }
                    label="Fim DHCP"
                    error={fieldErrors?.dhcp_range}
                    placeholder={`Ex: ${!value?.ips?.length || !Address4.isValid(value.ips[0]) ? null : new Address4(value.ips[0]).endAddressExclusive().address} -`}
                  />
                </GridItem>
              </>
            )}
          </Grid>
        </Accordion>
      </GridItem>

      <GridItem sm={12}>
        <Accordion
          title={
            <HBox>
              <span>Hotspot (Portal Cativo)</span>
              <Icon
                path={value?.hotspot_id != null ? mdiCheck : mdiCancel}
                size={0.75}
              />
            </HBox>
          }
        >
          <Grid>
            <GridItem sm={12}>
              <CampoCheckbox
                value={value?.hotspot_id != null}
                onChange={v =>
                  onChange({
                    ...value,
                    hotspot_id: v
                      ? (queryHotspots.data?.[0]?.id ?? null)
                      : null,
                  })
                }
                text="Habilitar Hotspot (Portal Cativo)"
                error={fieldErrors?.hotspot_id}
              />
            </GridItem>
            {value?.hotspot_id != null && (
              <GridItem sm={12}>
                <CampoSeletor
                  label="Portal Cativo"
                  value={String(value.hotspot_id ?? '')}
                  onChange={v => onChange({ ...value, hotspot_id: v ? Number(v) : null })}
                  options={(queryHotspots.data ?? [])
                    .filter(h => h.ativo)
                    .map(h => ({ id: String(h.id), label: h.nome }))}
                  error={fieldErrors?.hotspot_id}
                />
              </GridItem>
            )}
          </Grid>
        </Accordion>
      </GridItem>
    </Grid>
  );
};
